import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Close, Login, PersonAdd } from "@mui/icons-material";
import { Modal, Backdrop, Fade } from "@mui/material";

const LoginRequiredPopup = ({ isOpen, onClose, message = "Please login or signup to continue", onNavigate }) => {
  const navigate = useNavigate();

  const handleLogin = () => {
    onClose();
    if (onNavigate) {
      onNavigate();
    }
    navigate("/login");
  };

  const handleSignup = () => {
    onClose();
    if (onNavigate) {
      onNavigate();
    }
    navigate("/signup");
  };

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: StyledBackdrop }}
    >
      <Fade in={isOpen}>
        <ModalContainer>
          <PopupContent>
            <CloseButton onClick={onClose}>
              <Close />
            </CloseButton>
            
            <PopupHeader>
              <motion.div
                className="icon-container"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                🔐
              </motion.div>
              
              <motion.h2
                className="popup-title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                Login Required
              </motion.h2>
            </PopupHeader>

            <PopupBody>
              <motion.p
                className="message"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                {message}
              </motion.p>

              <motion.div
                className="action-buttons"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <motion.button
                  className="login-btn"
                  onClick={handleLogin}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Login />
                  Login
                </motion.button>
                
                <motion.button
                  className="signup-btn"
                  onClick={handleSignup}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <PersonAdd />
                  Sign Up
                </motion.button>
              </motion.div>

              <motion.button
                className="skip-btn"
                onClick={onClose}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                Maybe later
              </motion.button>
            </PopupBody>
          </PopupContent>
        </ModalContainer>
      </Fade>
    </Modal>
  );
};

export default LoginRequiredPopup;

const PopupContent = styled.div`
  position: relative;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-width: 450px;
  width: 90%;
  
  @media (max-width: 768px) {
    max-width: 95%;
    width: 95%;
    margin: 0 auto;
    border-radius: 16px;
  }
  
  @media (max-width: 480px) {
    max-width: 98%;
    width: 98%;
    border-radius: 12px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }
  
  svg {
    color: var(--text-color);
  }
`;

const PopupHeader = styled.div`
  position: relative;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark-color) 100%);
  padding: 40px 20px 30px;
  text-align: center;
  
  .icon-container {
    font-size: 3rem;
    margin-bottom: 20px;
  }
  
  .popup-title {
    color: white;
    font-size: 2rem;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const PopupBody = styled.div`
  padding: 30px;
  
  .message {
    color: var(--text-gray-color);
    line-height: 1.6;
    text-align: center;
    margin-bottom: 30px;
    font-size: 1.1rem;
  }

  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 20px;
    
    .login-btn, .signup-btn {
      padding: 16px 24px;
      border: none;
      border-radius: 50px;
      font-weight: 700;
      font-size: 1rem;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      min-height: 50px;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
      }
    }
    
    .login-btn {
      background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark-color) 100%);
      color: white;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    }
    
    .signup-btn {
      background: white;
      color: var(--primary-color);
      border: 2px solid var(--primary-color);
      
      &:hover {
        background: var(--primary-color);
        color: white;
      }
    }
  }
  
  .skip-btn {
    background: transparent;
    color: var(--text-gray-color);
    border: none;
    padding: 12px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    width: 100%;
    
    &:hover {
      color: var(--text-color);
      text-decoration: underline;
    }
  }
`;

const StyledBackdrop = styled(Backdrop)`
  background-color: rgb(0 0 0 / 0.5);
`;

const ModalContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  outline: none;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-height: 95vh;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  @media (max-width: 480px) {
    max-height: 98vh;
    padding: 10px;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`;
